import { useState, useCallback, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { 
  ZoomIn, 
  ZoomOut, 
  RotateCw, 
  RotateCcw,
  Upload, 
  Clipboard, 
  X, 
  Check,
  FlipHorizontal,
  FlipVertical,
  Maximize2,
  SkipForward,
  Loader2,
  Sparkles
} from 'lucide-react';
import { useTranslation } from '../lib/i18n';

// Helper function to create image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getRadianAngle = (degreeValue) => {
  return (degreeValue * Math.PI) / 180;
};

const rotateSize = (width, height, rotation) => {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0, flipH = false, flipV = false) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return null;

  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(data, 0, 0);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
  });
};

// Get original image as blob (without cropping)
const getOriginalAsBlob = async (imageSrc) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = image.width;
  canvas.height = image.height;
  ctx.drawImage(image, 0, 0);
  
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
  });
};

export const DocumentImageEditor = ({ 
  open, 
  onClose, 
  onComplete,
  onSkip,
  documentType, // 'identity_card' | 'driver_license' | 'passport'
  title
}) => {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);

  // Document type specific aspect ratios
  const aspectRatios = {
    identity_card: 1.586, // ID card ratio (85.6mm x 54mm)
    driver_license: 1.586,
    passport: 0.714 // Passport photo ratio (35mm x 45mm)
  };

  const aspectRatio = aspectRatios[documentType] || 1.586;

  // Listen for paste events
  useEffect(() => {
    const handlePaste = (e) => {
      if (!open || imageSrc) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let item of items) {
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          const blob = item.getAsFile();
          setOriginalFile(blob);
          const reader = new FileReader();
          reader.addEventListener('load', () => setImageSrc(reader.result));
          reader.readAsDataURL(blob);
          break;
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [open, imageSrc]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setOriginalFile(file);
      const reader = new FileReader();
      reader.addEventListener('load', () => setImageSrc(reader.result));
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0 && files[0].type.startsWith('image/')) {
      const file = files[0];
      setOriginalFile(file);
      const reader = new FileReader();
      reader.addEventListener('load', () => setImageSrc(reader.result));
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handlePasteButton = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        if (item.types.includes('image/png') || item.types.includes('image/jpeg')) {
          const type = item.types.find(t => t.startsWith('image/'));
          const blob = await item.getType(type);
          setOriginalFile(blob);
          const reader = new FileReader();
          reader.addEventListener('load', () => setImageSrc(reader.result));
          reader.readAsDataURL(blob);
          break;
        }
      }
    } catch (error) {
      console.error('Paste error:', error);
    }
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    
    setLoading(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation, flipH, flipV);
      if (croppedBlob) {
        // Create a File object from the blob
        const fileName = originalFile?.name || `${documentType}_edited.jpg`;
        const file = new File([croppedBlob], fileName, { type: 'image/jpeg' });
        onComplete(file);
      }
      handleReset();
      onClose();
    } catch (error) {
      console.error('Error processing image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipEdit = async () => {
    if (!imageSrc) return;
    
    setLoading(true);
    try {
      // Return original image without editing
      const blob = await getOriginalAsBlob(imageSrc);
      const fileName = originalFile?.name || `${documentType}.jpg`;
      const file = new File([blob], fileName, { type: 'image/jpeg' });
      onComplete(file);
      handleReset();
      onClose();
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setImageSrc(null);
    setOriginalFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setCroppedAreaPixels(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const rotateLeft = () => setRotation((r) => (r - 90 + 360) % 360);
  const rotateRight = () => setRotation((r) => (r + 90) % 360);

  const documentTypeLabels = {
    identity_card: t('employeeProfile.identityCard') || 'Kimlik Kartı',
    driver_license: t('employeeProfile.driverLicense') || 'Ehliyet',
    passport: t('employeeProfile.passport') || 'Pasaport'
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-500" />
            {title || `${documentTypeLabels[documentType]} Yükle`}
          </DialogTitle>
        </DialogHeader>

        {!imageSrc ? (
          /* Upload Zone */
          <div 
            ref={dropZoneRef}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              isDragging 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            tabIndex={0}
            data-testid="document-drop-zone"
          >
            <div className="flex flex-col items-center gap-4">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-colors ${
                isDragging ? 'bg-blue-100 dark:bg-blue-800' : 'bg-slate-100 dark:bg-slate-700'
              }`}>
                <Upload className={`h-10 w-10 ${isDragging ? 'text-blue-500' : 'text-slate-400'}`} />
              </div>
              
              <div>
                <p className="text-lg font-medium text-slate-700 dark:text-slate-200">
                  {t('documentEditor.dragDropTitle') || 'Belgeyi sürükleyip bırakın'}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {t('documentEditor.supportedFormats') || 'JPG, PNG, WEBP desteklenir'}
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-3 mt-2">
                <label className="cursor-pointer">
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handleFileSelect}
                    data-testid="document-file-input"
                  />
                  <Button type="button" variant="default" className="pointer-events-none bg-blue-600 hover:bg-blue-700">
                    <Upload className="h-4 w-4 mr-2" />
                    {t('documentEditor.selectFile') || 'Dosya Seç'}
                  </Button>
                </label>
                
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={handlePasteButton}
                >
                  <Clipboard className="h-4 w-4 mr-2" />
                  {t('documentEditor.pasteFromClipboard') || 'Panodan Yapıştır'}
                </Button>
              </div>

              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                <p className="flex items-center gap-2">
                  <Clipboard className="h-4 w-4" />
                  <strong>İpucu:</strong> {t('documentEditor.pasteHint') || 'Ctrl+V ile panodan direkt yapıştırabilirsiniz'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Editor Zone */
          <div className="space-y-4">
            {/* Crop Area */}
            <div className="relative h-72 bg-slate-900 rounded-lg overflow-hidden">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspectRatio}
                cropShape="rect"
                showGrid={true}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                restrictPosition={false}
                minZoom={0.3}
                maxZoom={4}
                style={{
                  containerStyle: {
                    transform: `scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`
                  }
                }}
              />
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap justify-center gap-2">
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={rotateLeft}
                title={t('documentEditor.rotateLeft') || 'Sola Döndür'}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={rotateRight}
                title={t('documentEditor.rotateRight') || 'Sağa Döndür'}
              >
                <RotateCw className="h-4 w-4" />
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => setFlipH(!flipH)}
                className={flipH ? 'bg-blue-100 border-blue-300' : ''}
                title={t('documentEditor.flipHorizontal') || 'Yatay Çevir'}
              >
                <FlipHorizontal className="h-4 w-4" />
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => setFlipV(!flipV)}
                className={flipV ? 'bg-blue-100 border-blue-300' : ''}
                title={t('documentEditor.flipVertical') || 'Dikey Çevir'}
              >
                <FlipVertical className="h-4 w-4" />
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => { setZoom(1); setRotation(0); setFlipH(false); setFlipV(false); setCrop({x:0, y:0}); }}
                title={t('documentEditor.reset') || 'Sıfırla'}
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            </div>

            {/* Zoom & Rotation Sliders */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-4">
              {/* Zoom */}
              <div className="flex items-center gap-4">
                <ZoomOut className="h-4 w-4 text-slate-500 flex-shrink-0" />
                <Slider
                  value={[zoom]}
                  min={0.3}
                  max={4}
                  step={0.1}
                  onValueChange={(value) => setZoom(value[0])}
                  className="flex-1"
                />
                <ZoomIn className="h-4 w-4 text-slate-500 flex-shrink-0" />
                <span className="text-sm text-slate-500 w-12 text-right">{Math.round(zoom * 100)}%</span>
              </div>

              {/* Rotation */}
              <div className="flex items-center gap-4">
                <RotateCw className="h-4 w-4 text-slate-500 flex-shrink-0" />
                <Slider
                  value={[rotation]}
                  min={0}
                  max={360}
                  step={1}
                  onValueChange={(value) => setRotation(value[0])}
                  className="flex-1"
                />
                <span className="text-sm text-slate-500 w-12 text-right">{rotation}°</span>
              </div>
            </div>

            {/* Change Image */}
            <Button 
              type="button" 
              variant="ghost" 
              size="sm"
              onClick={handleReset}
              className="w-full text-slate-500 hover:text-slate-700"
            >
              <X className="h-4 w-4 mr-2" />
              {t('documentEditor.changeImage') || 'Farklı Resim Seç'}
            </Button>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel') || 'İptal'}
          </Button>
          
          {imageSrc && (
            <Button 
              variant="ghost"
              onClick={handleSkipEdit}
              disabled={loading}
              className="text-slate-600"
            >
              <SkipForward className="h-4 w-4 mr-2" />
              {t('documentEditor.skipEdit') || 'Düzenlemeden Yükle'}
            </Button>
          )}
          
          <Button 
            onClick={handleSave} 
            disabled={!imageSrc || loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common.processing') || 'İşleniyor...'}
              </span>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {t('documentEditor.cropAndUpload') || 'Kes ve Yükle'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentImageEditor;
